import React, { useState } from 'react'
import { BookOpen, Loader2, Search, FolderInput, CheckCircle2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bff, toClientError } from '../services/bff'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// F2 — Confluence as a source: search the wiki (text/CQL), import pages (or a
// whole subtree) into the project with provenance. Renders only when the BFF
// has the connector configured (CONFLUENCE_BASE_URL + CONFLUENCE_PAT).
export default function ConfluenceImport({ project }) {
  const status = useQuery({
    queryKey: ['confluence', 'status'],
    queryFn: () => run(bff.get('/api/confluence/status')),
    staleTime: Infinity,
  })
  const [text, setText] = useState('')
  const search = useMutation({
    mutationFn: (q) => run(bff.get('/api/confluence/search', { params: { text: q }, timeout: 0 })),
  })
  const qc = useQueryClient()
  const imp = useMutation({
    mutationFn: (body) => run(bff.post('/api/confluence/import', body, { timeout: 0 })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rag', 'documents', project] }),
  })

  if (!status.data?.enabled) return null

  return (
    <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4 space-y-2">
      <div className="flex items-center gap-2 text-sm text-ink dark:text-mist">
        <BookOpen className="w-4 h-4 text-gold-deep dark:text-gold" />
        <span className="font-medium">Import from Confluence</span>
        <span className="text-xs text-ink-soft dark:text-mist/50 truncate">{status.data.base_url}</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && text.trim() && search.mutate(text.trim())}
          placeholder="Search pages… (text, or paste a page ID)"
          className="flex-1 h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        />
        <button
          onClick={() => text.trim() && search.mutate(text.trim())}
          disabled={!text.trim() || search.isPending}
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {search.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </div>
      {search.isError && <p className="text-xs text-red-600">{search.error?.message}</p>}
      {imp.isError && <p className="text-xs text-red-600">{imp.error?.message}</p>}
      {imp.isSuccess && (
        <p className="text-xs text-emerald-700 dark:text-emerald-300">
          Imported {imp.data.imported_count} page(s){imp.data.errors?.length ? `, ${imp.data.errors.length} failed` : ''}.
        </p>
      )}

      {(search.data?.results ?? []).map((p) => (
        <div key={p.id} className="flex items-center gap-3 rounded-lg border border-parchment-border bg-white/50 px-3 py-2 dark:border-midnight-600 dark:bg-midnight-800/40">
          <div className="min-w-0 flex-1">
            <a href={p.url} target="_blank" rel="noreferrer" className="text-sm text-ink dark:text-mist hover:underline truncate block">
              {p.title}
            </a>
            <div className="text-xs text-ink-soft dark:text-mist/50">
              {p.space} · v{p.version}
            </div>
          </div>
          <button
            onClick={() => imp.mutate({ project, page_id: p.id })}
            disabled={!project || imp.isPending}
            title={project ? `Import this page into "${project}"` : 'Pick a project first'}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
          >
            {imp.isPending && imp.variables?.page_id === p.id ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FolderInput className="w-3.5 h-3.5" />
            )}
            Import
          </button>
          <button
            onClick={() => imp.mutate({ project, page_id: p.id, recursive: true })}
            disabled={!project || imp.isPending}
            title="Import this page AND all its child pages (max 50)"
            className="shrink-0 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep disabled:opacity-40 dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
          >
            + subtree
          </button>
        </div>
      ))}
    </div>
  )
}
